import { FileOutput } from 'dtos/diligence/FileOutput';
import { ThumbnailsOutput } from 'dtos/diligence/ThumbnailsOutput';
import { DateTime } from 'luxon';
import { makeAutoObservable, reaction, runInAction, action } from 'mobx';
import { AuthStore } from 'stores/users/AuthStore';
import { UploadStore } from 'stores/UploadStore';
import { maxThumbnailsTries, serverUri } from 'utils/config';
import { LoadStatus } from 'utils/types';
import { nullsToUndefined } from 'utils/nullsToUndefined';

const hideStatusMs = 10000;

export class DiligenceFilesStore {
  uploadStore?: UploadStore;
  private showUploadStatusTimeout?: number;
  uploadStatus: 'none' | 'error' | 'sizeError' | 'success' = 'none';
  deleteStatus: LoadStatus = 'none';
  thumbnailsStatus: 'none' | 'loading' = 'none';
  private thumbnailsTries = 0;
  lastUploadCategory?: string;

  constructor(private readonly token: string, public files: FileOutput[], private readonly authStore: AuthStore) {
    makeAutoObservable(this, {}, { autoBind: true });

    reaction(() => this.files, this.getMissingThumbnails, { fireImmediately: true });
  }

  async uploadFiles(files: File[], rejections: any[] = []) {
    if (rejections.length) {
      this.uploadStatus = 'sizeError';
      this.showUploadStatus();
      return;
    }
    this.lastUploadCategory = undefined;
    this.hideUploadStatus();
    this.uploadStore = new UploadStore(this.token, files, {}, 75, 1500000, this.authStore);
    this.uploadStatus = 'none';
    try {
      const uploadedFiles = await this.uploadStore.upload();
      this.addFiles(uploadedFiles);
      runInAction(() => {
        this.uploadStatus = 'success';
        this.uploadStore = undefined;
        this.thumbnailsTries = 0;
        this.showUploadStatus();
      });
    } catch (err) {
      console.error(err);
      runInAction(() => {
        this.uploadStatus = 'error';
        this.uploadStore = undefined;
        this.showUploadStatus();
      });
    }
  }

  addFiles(files: FileOutput[]) {
    this.files = [...this.files, ...files];
  }

  removeFiles(files: FileOutput[], category?: any) {
    const idsToRemove = files.map((file) => file.id);
    this.files = this.files.filter((file) => idsToRemove.indexOf(file.id) === -1);
    if (category && this.sortedFilesForCategory(category.title).length === 0) {
      category.status = 'incomplete'; // also done on server
    }
  }

  get sortedFiles() {
    return [...this.files].sort((a, b) => new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime());
  }

  get latestDate(): DateTime | undefined {
    if (this.files.length === 0) return undefined;
    const latestDate = Math.max(...this.files.map((file) => new Date(file.lastModified).getTime()));
    return DateTime.fromMillis(latestDate);
  }

  sortedFilesForCategory(category: string) {
    const files = this.files.filter((file) => file.category === category);
    return [...files].sort((a, b) => new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime());
  }

  latestDateForCategory(category: string): DateTime | undefined {
    const files = this.files.filter((file) => file.category === category);
    if (files.length === 0) return undefined;
    const latestDate = Math.max(...files.map((file) => new Date(file.lastModified).getTime()));
    return DateTime.fromMillis(latestDate);
  }

  clearUploads() {
    this.uploadStore = undefined;
  }

  getMissingThumbnails = async () => {
    if (this.thumbnailsStatus === 'loading' || this.thumbnailsTries >= maxThumbnailsTries) return;
    const filesNeedingThumbnails = this.files.filter((file) => !file.thumbnail);
    if (filesNeedingThumbnails.length === 0) return;

    this.thumbnailsTries++;
    this.thumbnailsStatus = 'loading';
    try {
      const response = await this.authStore.fetchWithAuth(
        `${serverUri}/diligence/${encodeURIComponent(this.token)}/thumbnails`
      );
      if (!response.ok) {
        throw new Error();
      }
      const { files }: ThumbnailsOutput = nullsToUndefined(await response.json());
      runInAction(() => {
        this.thumbnailsStatus = 'none';
        this.files = this.files.map((file) => ({
          ...file,
          thumbnail: (files.find((thisFile) => thisFile.id === file.id) || file).thumbnail,
        }));
      });
    } catch (err) {
      console.error(err);
      this.thumbnailsStatus = 'none';
    }
  };

  deleteFile = async (file: FileOutput, category?: any) => {
    this.hideUploadStatus();
    this.deleteStatus = 'none';
    try {
      const response = await this.authStore.fetchWithAuth(
        `${serverUri}/diligence/${encodeURIComponent(this.token)}/files/${encodeURIComponent(file.id)}`,
        {
          method: 'DELETE',
        }
      );
      if (!response.ok) {
        throw new Error();
      }
      runInAction(() => {
        this.removeFiles([file], category);
        this.deleteStatus = 'success';
      });
    } catch (err) {
      console.error(err);
      runInAction(() => {
        this.deleteStatus = 'error';
      });
    }
  };

  showUploadStatus() {
    if (this.showUploadStatusTimeout !== undefined) {
      window.clearTimeout(this.showUploadStatusTimeout);
    }
    this.showUploadStatusTimeout = window.setTimeout(
      action(() => {
        this.showUploadStatusTimeout = undefined;
      }),
      hideStatusMs
    );
  }

  hideUploadStatus() {
    if (this.showUploadStatusTimeout !== undefined) {
      window.clearTimeout(this.showUploadStatusTimeout);
      this.showUploadStatusTimeout = undefined;
    }
  }

  updateCategory = async (file: FileOutput, category: string) => {
    runInAction(() => {
      this.files = this.files.map((f) => ({
        ...f,
        category: f.id === file.id ? category : f.category,
      }));
    });
    try {
      const response = await this.authStore.fetchWithAuth(
        `${serverUri}/diligence/${encodeURIComponent(this.token)}/files/${encodeURIComponent(file.id)}/category`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ category: category || undefined }),
        }
      );
      if (!response.ok) {
        throw new Error('Failed to update category');
      }
    } catch (err) {
      console.error('Failed to update file category:', err);
      throw err;
    }
  };

  get showingUploadStatus() {
    return this.showUploadStatusTimeout !== undefined;
  }
}
